import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useOrganization } from '@/hooks/useOrganization';
import { listOSByDateRange } from '@/services/agenda.service';
import {
  reagendarOS,
  ReagendarOSBlockedStatusError,
} from '@/services/ordens-servico.service';
import { sanitizeSupabaseError } from '@/lib/errorHandler';
import { captureError } from '@/lib/sentry';

export function useAgendaOS(range: { start: Date; end: Date }) {
  const { organizationId } = useOrganization();
  const startIso = range.start.toISOString();
  const endIso = range.end.toISOString();

  return useQuery({
    queryKey: ['agenda', organizationId, startIso, endIso],
    queryFn: () => listOSByDateRange({ orgId: organizationId!, start: range.start, end: range.end }),
    enabled: !!organizationId,
  });
}


export interface ReagendarOSAvancadoInput {
  osId: string;
  dataAgendada: Date;
  prestadorId?: string | null;
  duracaoEstimadaMinutos?: number;
  motivo: string;
}

type AgendaCacheSnapshot = Array<[readonly unknown[], unknown]>;

export function useReagendarOSAvancado() {
  const queryClient = useQueryClient();
  const { requireOrgId } = useOrganization();

  return useMutation({
    mutationFn: async (input: ReagendarOSAvancadoInput) => {
      return reagendarOS({
        id: input.osId,
        orgId: requireOrgId(),
        novaData: input.dataAgendada.toISOString(),
        prestadorId: input.prestadorId,
        duracaoEstimadaMinutos: input.duracaoEstimadaMinutos,
        motivo: input.motivo,
      });
    },
    onMutate: async (input) => {
      await queryClient.cancelQueries({ queryKey: ['agenda'] });

      const snapshot: AgendaCacheSnapshot = [...queryClient.getQueriesData({ queryKey: ['agenda'] })];

      const targetIso = input.dataAgendada.toISOString();
      const applyPatch = (os: Record<string, unknown>) => {
        if (os.id !== input.osId) return os;
        const patched: Record<string, unknown> = { ...os, data_agendada: targetIso };
        if (input.prestadorId !== undefined) patched.prestador_id = input.prestadorId;
        if (input.duracaoEstimadaMinutos !== undefined)
          patched.duracao_estimada_minutos = input.duracaoEstimadaMinutos;
        return patched;
      };

      queryClient.setQueriesData<Record<string, unknown>[] | undefined>(
        { queryKey: ['agenda'] },
        (old) => (old ? old.map(applyPatch) : old),
      );

      return { snapshot };
    },
    onError: (error: Error, _input, context) => {
      if (context?.snapshot) {
        for (const [key, data] of context.snapshot) {
          queryClient.setQueryData(key, data);
        }
      }
      if (error instanceof ReagendarOSBlockedStatusError) {
        toast.error('Esta OS não pode ser remarcada no estado actual.');
        return;
      }
      captureError(error, { extra: { hook: 'useReagendarOSAvancado' } });
      toast.error(sanitizeSupabaseError(error));
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['agenda'] });
      queryClient.invalidateQueries({ queryKey: ['ordens-servico'] });
    },
    onSuccess: () => {
      toast.success('OS actualizada.');
    },
  });
}
